<template>
  <DashboardPanel
    panel-id="v-pills-personal"
    aria-labelledby="v-pills-personal-tab"
    title="Personal details"
    subtitle="Contact info, location, and profile summary"
  >
    <template #header-actions>
      <ProfileActionBtn variant="edit" @click.prevent="toggleProfileEdit">Edit</ProfileActionBtn>
    </template>
      <div id="display_profile" v-if="!edit_profile && user_details" class="hc-detail-groups">
        <div class="hc-detail-group">
          <h4 class="hc-detail-group__title">About</h4>
          <ProfileDetailGrid single>
            <ProfileDetailItem label="About me" :value="user_details.resume_summary" wide />
          </ProfileDetailGrid>
        </div>

        <div class="hc-detail-group">
          <h4 class="hc-detail-group__title">Basic info</h4>
          <ProfileDetailGrid>
            <ProfileDetailItem label="Name" :value="e_user.name" required />
            <ProfileDetailItem label="Gender" :value="user_gender" />
            <ProfileDetailItem label="Date of birth" :value="formattedDob" />
            <ProfileDetailItem label="Marital status" :value="user_marital_status" />
          </ProfileDetailGrid>
        </div>

        <div class="hc-detail-group">
          <h4 class="hc-detail-group__title">Location</h4>
          <ProfileDetailGrid>
            <ProfileDetailItem label="Current city" :value="user_details.user_city && user_details.user_city.city_name" required />
            <ProfileDetailItem label="Sub location" :value="user_details.sub_location && user_details.sub_location.sublocation_name" />
          </ProfileDetailGrid>
        </div>

        <div class="hc-detail-group">
          <h4 class="hc-detail-group__title">Professional</h4>
          <ProfileDetailGrid>
            <ProfileDetailItem label="Job role" :value="user_role.join(', ')" required />
            <ProfileDetailItem label="Languages known" :value="languagesKnown" required />
            <ProfileDetailItem label="Skills" :value="skillsList" required wide />
          </ProfileDetailGrid>
        </div>

        <div class="hc-detail-group">
          <h4 class="hc-detail-group__title">Contact</h4>
          <ProfileDetailGrid>
            <ProfileDetailItem label="Current address" :value="user_details.current_address" wide />
            <ProfileDetailItem label="Permanent address" :value="user_details.permanent_address" wide />
            <ProfileDetailItem label="Alternate contact" :value="user_details.contact_no" />
          </ProfileDetailGrid>
        </div>
      </div>
      <div id="edit_profile" class="hc-profile-edit-form" v-if="edit_profile">
        <v-form ref="personal_form" v-model="valid" lazy-validation>
          <div class="hc-detail-groups">
            <div class="hc-detail-group">
              <h4 class="hc-detail-group__title">About</h4>
              <ProfileDetailGrid single>
                <ProfileEditItem label="About me" wide>
                  <v-textarea id="title" v-model="e_user.resume_summary" :rules="titleRules" placeholder="Describe yourself in max 120 characters" rows="3" outlined dense hide-details counter="120" />
                </ProfileEditItem>
              </ProfileDetailGrid>
            </div>
            <div class="hc-detail-group">
              <h4 class="hc-detail-group__title">Basic info</h4>
              <ProfileDetailGrid>
                <ProfileEditItem label="Name" required>
                  <v-text-field v-model="e_user.name" type="text" placeholder="Name" :rules="[v => !!v || 'Select name!']" outlined dense hide-details />
                </ProfileEditItem>
                <ProfileEditItem label="Gender">
                  <v-autocomplete name="gender" v-model="e_user.gender" :items="gender.options" item-text="text" item-value="value" placeholder="Gender" clearable outlined dense hide-details />
                </ProfileEditItem>
                <ProfileEditItem label="Date of birth">
                  <input class="hc-field-input" v-model="e_user.dob" type="date" name="dob">
                </ProfileEditItem>
                <ProfileEditItem label="Marital status">
                  <v-autocomplete name="marital_status" v-model="e_user.marital_status" :items="maritalStatus.options" item-text="text" item-value="value" placeholder="Marital status" clearable outlined dense hide-details />
                </ProfileEditItem>
              </ProfileDetailGrid>
            </div>
            <div class="hc-detail-group">
              <h4 class="hc-detail-group__title">Location</h4>
              <ProfileDetailGrid>
                <ProfileEditItem label="Current city" required>
                  <v-autocomplete v-model="e_user.city" placeholder="Current city" :items="states" item-text="text" item-value="id" :rules="[v => !!v || 'Select city!']" @change="showSubcity" clearable outlined dense hide-details>
                    <template slot="item" slot-scope="data">
                      <template v-if="typeof(data.item) != 'object'">
                        <v-list-item-content v-text="data.item.text"/>
                      </template>
                      <template v-else>
                        <v-list-item-content>
                          <v-list-item-title v-html="data.item.text"/>
                          <v-list-item-sub-title v-html="data.item.group"/>
                        </v-list-item-content>
                      </template>
                    </template>
                  </v-autocomplete>
                </ProfileEditItem>
                <ProfileEditItem v-if="subCity" label="Sub location">
                  <v-autocomplete v-model="e_user.sub_location" placeholder="Sub location" :items="sublocations" item-value="value" item-text="text" clearable outlined dense hide-details />
                </ProfileEditItem>
              </ProfileDetailGrid>
            </div>
            <div class="hc-detail-group">
              <h4 class="hc-detail-group__title">Professional</h4>
              <ProfileDetailGrid>
                <ProfileEditItem label="Job role" required wide>
                  <v-autocomplete v-model="e_user.jobrole" placeholder="Job role" :rules="[v => !!v.length || 'Select jobrole!']" :items="jobrole" item-text="text" item-value="value" multiple deletable-chips chips clearable outlined dense hide-details />
                </ProfileEditItem>
                <ProfileEditItem label="Languages known" required>
                  <v-autocomplete v-model="e_user.languages_known" :items="languages" item-text="text" item-value="text" :rules="[v => !!v.length || 'Select languages!']" placeholder="Languages known" deletable-chips clearable outlined dense hide-details chips multiple />
                </ProfileEditItem>
                <ProfileEditItem label="Skills" required wide>
                  <v-autocomplete id="label-experience" placeholder="Skills" v-model="e_user.skils" :items="skills" item-text="text" item-value="text" :rules="[v => !!v.length || 'Select skills!']" clearable outlined dense hide-details multiple chips deletable-chips />
                </ProfileEditItem>
              </ProfileDetailGrid>
            </div>
            <div class="hc-detail-group">
              <h4 class="hc-detail-group__title">Contact</h4>
              <ProfileDetailGrid>
                <ProfileEditItem label="Current address" wide>
                  <v-textarea id="current_address" placeholder="Current address" v-model="e_user.current_address" rows="2" outlined dense hide-details />
                </ProfileEditItem>
                <ProfileEditItem label="Permanent address" wide>
                  <v-textarea id="permenant_address" v-model="e_user.permanent_address" placeholder="Permanent address" rows="2" outlined dense hide-details />
                </ProfileEditItem>
                <ProfileEditItem label="Alternate contact">
                  <v-text-field name="contact-number" placeholder="Alternate contact number" type="tel" v-model="e_user.alternate_contact_no" outlined dense hide-details />
                </ProfileEditItem>
              </ProfileDetailGrid>
            </div>
          </div>
          <p v-if="error" class="hc-profile-edit-form__error">{{ err_msg }}</p>
          <ProfileEditFooter :saving="loading" @cancel="cancelProfileEdit" @save="updateProfile" />
        </v-form>
      </div>
      <div class="hc-panel__section">
        <div class="hc-panel__section-head">
          <h3>Resume</h3>
          <ProfileActionBtn variant="edit" @click="toggleResumeEdit">Edit</ProfileActionBtn>
        </div>
        <div class="hc-panel__section-body hc-profile-details">
      <form>
        <div v-if="!resumeEdit&&user_details">
          <ProfileDetailGrid single>
            <ProfileDetailItem label="Resume" wide>
              <div v-if="hasResume" class="hc-resume-status">
                <span class="hc-resume-status__badge">Resume uploaded</span>
                <p v-if="resumeFileName" class="hc-resume-status__filename">{{ resumeFileName }}</p>
                <div class="hc-resume-links">
                  <a target="_blank" rel="noopener" :href="resumeUrl">Download</a>
                  <a href="#" class="text-primary" @click.prevent="copyToClipboard(resumeUrl)">Copy link</a>
                </div>
              </div>
              <span v-else class="hc-detail-item__value hc-detail-item__value--empty">Not uploaded</span>
            </ProfileDetailItem>
          </ProfileDetailGrid>
        </div>

        <div v-if="resumeEdit" class="hc-profile-edit-form">
          <ProfileDetailGrid single>
            <ProfileEditItem label="Resume" wide>
              <v-file-input v-model="resume" placeholder="Choose file" :append-icon="$icons.file" prepend-icon="" show-size outlined dense hide-details accept=".doc,.docx,.rtf,.pdf" />
              <p class="hc-profile-edit__hint">Accepted formats: doc, docx, rtf, pdf</p>
            </ProfileEditItem>
          </ProfileDetailGrid>
          <p v-if="resume_error" class="hc-profile-edit-form__error">{{ resume_err_msg }}</p>
          <ProfileEditFooter @cancel="cancelResumeEdit" @save="updateResume" />
        </div>
      </form>
        </div>
      </div>
    <ProfileToast ref="profileToast" />
  </DashboardPanel>
</template>

<script>
import AwsUpload from "../../utils/s3upload"
import validation from '../libraries/validFormat' ;
import Actions from '../libraries/apiActions.js';
import DashboardPanel from './Dashboard/DashboardPanel.vue';
import ProfileDetailGrid from './Dashboard/ProfileDetailGrid.vue';
import ProfileDetailItem from './Dashboard/ProfileDetailItem.vue';
import ProfileToast from './Dashboard/ProfileToast.vue';
import ProfileActionBtn from './Dashboard/ProfileActionBtn.vue';
import ProfileEditFooter from './Dashboard/ProfileEditFooter.vue';
import ProfileEditItem from './Dashboard/ProfileEditItem.vue';
import '../assets/hire.css';
export default {
  name: "PersonalDetails",
  components: { DashboardPanel, ProfileDetailGrid, ProfileDetailItem, ProfileToast, ProfileActionBtn, ProfileEditFooter, ProfileEditItem },
  props: ["user"],
  data() {
    return {
      user_details: this.user?.personal_details,
      user_career:this.user?.careerPreferencesDetail,
      user_city : null,
      user_state : null,
      languages:[],
      skills:[],
      jobrole:[],
      u_jobrole:[],
      t_city:null,
      gender:{
        options:[
          {value:1, text:'Male'},
          {value:2, text:'Female'},
          {value:3, text:'Both'}
        ]
      },
      maritalStatus:{
        options:[
          {value:1, text:'Single'},
          {value:2, text:'Married'}
        ]
      },
      communication_rate:{
        options:[
          {value:1, text:'Low'},
          {value:2, text:'Moderate'},
          {value:3, text:'Good'},
          {value:4, text:'Very Good'}          
        ]
      },
      e_user:{
        resume_summary:'',
        name : null,
        city : null,
        sub_location : null,
        gender : null,
        dob : null,
        marital_status : null,
        languages_known : [],
        communication_rating : 1,
        jobrole: [],
        skils: [],
        current_address: null,
        permanent_address: null,
        alternate_contact_no: null
      },
      user_gender:null,
      user_marital_status:null,
      user_role:[],
      limit_error:false,
      error_msg:'',
      subCity: false,
      can_update: 0 ,
      states:[],
      sublocations:[],
      resumeEdit: false,
      resume: null,
      update:{
        resume:null
      },
      action: new Actions(),
      validate: new validation(),
      resume_loading: false,
      profile_loading: false,
      valid:true,
      loading: false,
      edit_profile:false,
      titleRules: [
        v => !!v || 'Enter valid email', 
        v => (v && v.length <= 120) || 'Max 120 characters'
      ],
      error:false,
      err_msg:null,
      resume_error:false,
      resume_err_msg:null,
    };
  },
  mounted(){
    // this.e_user.sub_location = this.user_details.sub_location.sublocation_id
    this.user_city = this.user_details?.user_city?.city_id
    // this.subCity = (this.user_details.sub_location.sublocation_name) ? true:false
    // document.getElementById('current_address').text = this.e_user.current_address?this.e_user.current_address:' ',
    // document.getElementById('permanent_address').text = this.e_user.permanent_address?this.e_user.permanent_address:' '
  },
  computed: {
    formattedDob() {
      if (!this.user_details?.dob) return '';
      return this.user_details.dob.split('-').reverse().join('-');
    },
    languagesKnown() {
      if (!this.user?.languages_known?.language_known) return '';
      try {
        return JSON.parse(this.user.languages_known.language_known).join(', ');
      } catch (e) {
        return '';
      }
    },
    skillsList() {
      if (!this.user_details?.skills) return '';
      try {
        const parsed = JSON.parse(this.user_details.skills);
        if (Array.isArray(parsed)) return parsed.join(', ');
        return String(parsed);
      } catch (e) {
        return this.user_details.skills;
      }
    },
    hasResume() {
      return !!(this.user_details?.resume_path || this.$cookies.get('hire-seeker')?.resume === 'yes');
    },
    resumeUrl() {
      const path = this.user_details?.resume_path || this.$cookies.get('hire-seeker')?.resume_path;
      return path ? this.$S3_bucket_url + path : '';
    },
    resumeFileName() {
      const path = this.user_details?.resume_path || this.$cookies.get('hire-seeker')?.resume_path;
      return this.getResumePath(path) || '';
    },
  },
  beforeMount(){
    this.getLanguages();
    this.getSkills();
    this.getJobrole();
    this.getStates();
    this.setUserDetails();
  },
  methods: {
    setUserDetails(){
      this.e_user.name = this.user.name;
      this.e_user.resume_summary = this.user_details?.resume_summary;
      this.e_user.city = this.user_details?.user_city.city_id;
      if(this.e_user.city){
        this.getCities();
      }
      if(this.user_details?.sub_location){
        this.e_user.sub_location = this.user_details?.sub_location.sublocation_id;
      }
      this.e_user.gender = this.user_details?.gender;
      if(this.e_user.gender != null){
        this.getGender(this.e_user.gender);
      }
      this.e_user.dob = this.user_details?.dob;
      this.e_user.marital_status = this.user_details?.marital_status;
      if(this.e_user.marital_status != null){
        this.getMaritalStatus(this.e_user.marital_status);
      }
      if(this.user.languages_known){
        this.e_user.languages_known = JSON.parse(this.user.languages_known.language_known);
      }
      if(this.user_career?.fst_prefered_role){
        this.setJobRole();
      }
      if(this.user_details?.skills){
        var skills = this.user_details.skills.split(',');
        skills = (JSON.parse(skills).replace(/[^a-zA-Z0-9, ]/g, " ")).split(',');
        skills.forEach((skill) => {
          this.e_user.skils.push(skill.trim());
        })
      }
      this.e_user.current_address = this.user_details?.current_address ;
      this.e_user.permanent_address = this.user_details?.permanent_address;
      this.e_user.alternate_contact_no = this.user_details?.contact_no;
    },
    async copyToClipboard(link){
      await navigator.clipboard.writeText(link);
    },
    EditProfile: function () {
      var display_profile = document.getElementById('display_profile')
      var edit_profile = document.getElementById('edit_profile')

      display_profile.style.display= (display_profile.style.display=='none')? 'block' : 'none';
      edit_profile.style.display= (edit_profile.style.display=='none')? 'block' : 'none';
      // this.showSubcity();
      // this.e_user.sub_location=null;
      this.e_user.resume_summary = (this.user_details?.resume_summary)?(this.user_details?.resume_summary):'';
    },
    getStates: async function () {
      await this.action.getStates().then(async (data) => {
        this.states.push({header: 'Top Metropolitan Cities'})
        await data.states.forEach(async (element) => {
          await element.cities.forEach(city => {
            if(city.top_city == 1){
              this.states.push({text:city.city_name,id:city.city_id, s_id:city.state_id,group:'Top Metropolitan Cities'})
            }
          });
          this.states.push({divider:true})
          this.states.push({header: element.state_name});
          element.cities.forEach(city => {
            if(city.top_city == 0){
              this.states.push({text:city.city_name,id:city.city_id, s_id:city.state_id,group:element.state_name})
            }
        });
      });
      });
    },
    showSubcity: function () {
      this.subCity = false;
      if (this.e_user.city != null) {
        this.getCities();
      }
    },
    getCities: function(){
      this.sublocations = [];
      this.action.getSubcity(this.e_user.city).then((data) => {
        if(data.sublocations){
          this.subCity = true;
          data.sublocations.forEach((element) => {
            this.sublocations.push({
              value: element.sublocation_id,
              text: element.sublocation_name,
            });
          });
        }
      });
    },
    getLanguages(){
        this.action.getLanguages().then(data => {
          data.languages.forEach((element) => {
            this.languages.push({
              value:element.id,
              text:element.language
            })
          })
        }).catch(err =>{
          console.log(err);
        })
    },
    getSkills: function(){
      this.action.getSkills().then(data => {
        data.skills.forEach((element) => {
          this.skills.push({
            value:element.id,
            text:element.skill
          })
        })
      }).catch(err =>{
        console.log(err);
      })
    },
    getJobrole: function(){
      this.action.getJobrole().then((data) => {
        data.jobroles.forEach((element) => {
          this.jobrole.push({
            value: element.role_id,
            text: element.role,
          });
        });
        // this.setUserJobrole();
      });
    },
    setJobRole: function(){
      this.$store.state.jobrole.jobrole.jobrole.forEach((element) => {
          if(element.value == this.user_career.fst_prefered_role){
            this.user_role.push(element.text)
          }
          if(element.value == this.user_career.snd_prefered_role){
            this.user_role.push(element.text)
          }
          if(element.value == this.user_career.trd_prefered_role){
            this.user_role.push(element.text)
          }
      })
        if(this.user_career.fst_prefered_role){
          this.e_user.jobrole.push(this.user_career.fst_prefered_role);
        }
        if(this.user_career.snd_prefered_role){
          this.e_user.jobrole.push(this.user_career.snd_prefered_role);
        }
        if(this.user_career.trd_prefered_role){
          this.e_user.jobrole.push(this.user_career.trd_prefered_role);
        }
    },
    getGender(id){
      this.gender.options.forEach((g) => {
        if(g.value == id){
          this.user_gender = g.text;
        }
      })
    },
    getMaritalStatus(id){
      this.maritalStatus.options.forEach((m) => {
        if(m.value == id){
          this.user_marital_status = m.text; 
        }
      })
    },
    limiter: function(e){
      if (e.length > 4) {
        this.limit_error = true;
        this.error_msg = "Select maximum 4 language known";
        e.pop();
      }else{
        this.limit_error = false;
        this.error_msg = ""
      }
    },
    updateProfile: function(e) {
      if (e && e.preventDefault) e.preventDefault();
      this.can_update = 0;
      this.loading= true;
      this.error = false;
      this.error_msg = null;

      this.valid = this.$refs.personal_form.validate();
      if (this.valid) {
          let token = this.$cookies.get("hire-seeker").token;
          this.e_user.skils = (this.e_user.skils)?((typeof this.e_user.skils!= 'string')?this.e_user.skils.join():this.e_user.ski):null;
          this.action.updateSeekerProfile(this.e_user,token).then(data =>{
            if(data.message == 'Personal Deatails Updated'){
              this.loading = false;
              this.$refs.profileToast.show('Personal details saved', 'success', {
                duration: 2500,
                onClose: () => location.reload(),
              });
            }
          }).catch(err =>{
            console.log(err);
            this.loading = false;
            this.error = true;
            if(window.navigator.onLine){
              this.err_msg = "System error! Please try again.";
            }else{
              this.err_msg = "Network error! Please check your internet connection.";
            }
          })
      }else{
        this.loading = false;
        this.error = true;
        if(window.navigator.onLine){
          this.err_msg = "Fill all mandatory fields.";
        }else{
          this.err_msg = "Network error! Please check your internet connection.";
        }
      }
    },
    cancelProfileEdit() {
      this.e_user.skils = [];
      this.e_user.jobrole = [];
      this.user_role = [];
      this.setUserDetails();
      this.setJobRole();
      this.error = false;
      this.err_msg = null;
      this.loading = false;
      this.edit_profile = false;
      if (this.$refs.personal_form) {
        this.$refs.personal_form.resetValidation();
      }
    },
    toggleProfileEdit() {
      if (this.edit_profile) {
        this.cancelProfileEdit();
      } else {
        this.edit_profile = true;
      }
    },
    cancelResumeEdit() {
      this.resume = null;
      this.resume_error = false;
      this.resume_err_msg = null;
      this.resumeEdit = false;
    },
    toggleResumeEdit() {
      if (this.resumeEdit) {
        this.cancelResumeEdit();
      } else {
        this.resumeEdit = true;
      }
    },
    getResumePath: function(path) {
      if(path != null){
        let res = path.split("/");
        return res[res.length-1]
      }
    },
    updateResume: async function(){
      this.resume_error = false;
      this.resume_err_msg = null;
      
      let token = this.$cookies.get("hire-seeker").token;
      if(this.resume != null){
        const file = this.resume;
        const awsUploader = new AwsUpload();
          try {
            var extension = this.resume.name.split('.')[this.resume.name.split('.').length-1]
            var d = new Date();
            var month = d.getMonth() + 1;
            var day = d.getDate();
            var current_date =d.getFullYear() +"/" +(month < 10 ? "0" : "") +month +"/" +(day < 10 ? "0" : "") +day;
            var timestamp = Math.floor(Date.now() / 1000);
            var filename = 'resume/'+ current_date+"/"+this.user.name + timestamp + "."+extension;
            const response = await awsUploader.uploadToS3(file, filename);
            this.update.resume = response.url;
            this.action.updateResume(this.update.resume,token).then(data =>{
              if(data.message == "Resume Updated"){
                this.resumeEdit = false;
                this.resume = false;
                let cookie = this.$cookies.get('hire-seeker');
                let updated_cookie = {token:cookie.token,name:cookie.name,visume_path:cookie.visume_path,resume:'yes',resume_path:this.update.resume,alert:cookie.alert};
                this.$cookies.remove("hire-seeker");
                this.$cookies.set("hire-seeker",updated_cookie);
                this.$refs.profileToast.show('Resume uploaded successfully', 'success', {
                  duration: 2500,
                  onClose: () => location.reload(),
                });
              }else{
                this.resume_error = true;
                this.resume_err_msg = "System error! Please try again.";
              }
            }).catch(err =>{
              console.log(err);
              this.resume_error = true;
              if(window.navigator.onLine){
                this.resume_err_msg = "System error! Please try again.";
              }else{
                this.resume_err_msg = "Network error! Please check your internet connection.";
              }
            })
          } catch (error) {
            console.error('Failed to upload file to S3:', error);
          }
      }
    }
  },
};
</script>

<style scoped>
input{padding: 28px 0px 28px 15px !important;}.error-feedback{position: absolute;}v-text-field__details{display: block !important;}
</style>